/**
 * MailThreadPanel — thread mail bidirezionale per ordine (montato in MailPage,
 * colonna destra). Il wrapper-pagina standalone MailThreadPage e' stato rimosso
 * (nessuna route lo montava, audit 2026-07-06): l'unico consumer e' MailPage.
 *
 * Layout: lista bubble (outbound a sinistra, inbound a destra), bottone
 * "Rispondi" in basso che apre MailComposeDialog. Righe con status="failed"
 * mostrano un bottone "Riprova" che richiama sendSupplierReply con i valori
 * della riga originale.
 */
import { useState } from "react";
import { ErrorBanner } from "@/shared/ui/ErrorBanner";
import { prettifyApiError } from "@/shared/api";
import { useMailThread, sendSupplierReply } from "./api";
import type { MailThreadRow } from "./api";
import { useMailCompose } from "./MailComposeDialog";
import type { MailComposePayload } from "./MailComposeDialog";

// ---------------------------------------------------------------------------
// Sub-component: singola bolla del thread
// ---------------------------------------------------------------------------

interface ThreadBubbleProps {
  row: MailThreadRow;
  orderRef: string;
  onRetry: (row: MailThreadRow, index: number) => void;
  retryBusy: boolean;
  index: number;
}

function formatTs(iso: string): string {
  try {
    return new Intl.DateTimeFormat("it-IT", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

function ThreadBubble({ row, onRetry, retryBusy, index }: ThreadBubbleProps) {
  const isOutbound = row.direction === "outbound";
  const isFailed = row.status === "failed";

  return (
    <div
      data-direction={row.direction}
      className={`flex ${isOutbound ? "justify-start" : "justify-end"} mb-3`}
    >
      <div
        className={`max-w-[75%] rounded-2xl border px-4 py-3 shadow-[0_1px_2px_rgba(31,27,22,0.05)] ${
          isOutbound ? "border-line bg-card text-ink" : "border-accent/30 bg-accent-soft text-ink"
        } ${isFailed ? "border-red/40 bg-red-soft" : ""}`}
      >
        {/* Subject */}
        <p className="mb-1 truncate font-display text-[13.5px] font-semibold text-ink">
          {row.subject}
        </p>

        {/* Body: rispetta i newline */}
        <p className="whitespace-pre-wrap text-[13.5px] leading-relaxed text-ink">{row.body}</p>

        {/* Allegati */}
        {row.attachment_names.length > 0 && (
          <ul className="mt-2 space-y-0.5">
            {row.attachment_names.map((name) => (
              <li key={name} className="flex items-center gap-1 text-[12px] text-ink-2">
                <svg
                  width="10"
                  height="10"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                  className="shrink-0"
                >
                  <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                </svg>
                <span>{name}</span>
              </li>
            ))}
          </ul>
        )}

        {/* Footer: timestamp + stato fallito + riprova */}
        <div className="mt-2 flex items-center justify-between gap-3">
          <time dateTime={row.ts} className="text-[11.5px] tabular-nums text-ink-2">
            {formatTs(row.ts)}
          </time>
          <div className="flex items-center gap-2">
            {isFailed && <span className="text-[10.5px] font-medium text-red">Invio fallito</span>}
            {isFailed && (
              <button
                type="button"
                onClick={() => onRetry(row, index)}
                disabled={retryBusy}
                aria-busy={retryBusy}
                className="rounded-full border border-line-2 bg-card px-3 py-1 text-[12px] font-semibold text-ink hover:border-ink-4 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {retryBusy ? "Riprovando…" : "Riprova"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// MailThreadPanel — contenuto del thread (bolle + Rispondi), senza routing.
// Estratto da MailThreadPage (Task 12) per essere montato anche dentro
// MailPage (colonna destra) mantenendo MailThreadPage/:ref come route
// standalone invariata per i test esistenti.
// ---------------------------------------------------------------------------

export function MailThreadPanel({ orderRef }: { orderRef: string }) {
  const { rows, isLoading, error, refresh } = useMailThread(orderRef);

  // Stato per "Riprova" su singola riga fallita
  const [retryBusyId, setRetryBusyId] = useState<string | null>(null);
  const [retryError, setRetryError] = useState<string | null>(null);

  // Hook compose per il dialog Rispondi
  const { open: openCompose, dialog: composeDialog } = useMailCompose(sendSupplierReply);

  // -------------------------------------------------------------------------
  // Handlers
  // -------------------------------------------------------------------------

  async function handleRetry(row: MailThreadRow, index: number) {
    const rowId = row.message_id ?? `${row.ts}-${index}`;
    if (retryBusyId) return;

    setRetryBusyId(rowId);
    setRetryError(null);

    // Stesso destinatario della riga fallita: con "" il backend ripartiva da
    // capo e la riprova poteva andare altrove (o non partire affatto).
    const payload: MailComposePayload = {
      recipient: row.recipient ?? "",
      subject: row.subject,
      body: row.body,
      packages_count: null,
    };

    try {
      await sendSupplierReply(orderRef, payload);
      setRetryBusyId(null);
      try {
        await refresh();
      } catch {
        // refresh fallita: non blocca l'utente
      }
    } catch (err: unknown) {
      setRetryError(prettifyApiError(err));
      setRetryBusyId(null);
    }
  }

  function handleOpenCompose() {
    setRetryError(null);
    // Suggerisci un Re: basato sull'ultimo inbound, o default
    const lastInbound = [...rows].reverse().find((r) => r.direction === "inbound");
    const reSubject = lastInbound
      ? lastInbound.subject.startsWith("Re:")
        ? lastInbound.subject
        : `Re: ${lastInbound.subject}`
      : `Re: Ordine ${orderRef}`;

    openCompose({
      orderRef,
      initialRecipient: "",
      initialSubject: reSubject,
      initialBody: "",
      attachmentNames: [],
      packagesCount: null,
      onSuccess: () => void refresh(),
    });
  }

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <>
      {/* Feedback errore retry */}
      <ErrorBanner message={retryError} className="mb-3 inline-flex items-start leading-relaxed" />

      {/* Stato loading */}
      {isLoading && (
        <div
          role="status"
          aria-live="polite"
          className="flex items-center justify-center py-16 text-[13px] text-ink-2"
        >
          Caricamento thread…
        </div>
      )}

      {/* Errore fetch */}
      {!isLoading && error && (
        <div role="alert" className="flex items-center justify-center py-16 text-sm text-red">
          {error}
        </div>
      )}

      {/* Thread vuoto */}
      {!isLoading && !error && rows.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 text-[13px] text-ink-2">
          <p>Nessun messaggio per questo ordine.</p>
        </div>
      )}

      {/* Lista bolle */}
      {!isLoading && !error && rows.length > 0 && (
        <div className="max-w-3xl mx-auto">
          {rows.map((row, index) => {
            const rowId = row.message_id ?? `${row.ts}-${index}`;
            return (
              <ThreadBubble
                key={rowId}
                row={row}
                orderRef={orderRef}
                onRetry={(r, i) => void handleRetry(r, i)}
                retryBusy={retryBusyId === rowId}
                index={index}
              />
            );
          })}
        </div>
      )}

      {/* Bottone Rispondi — sempre visibile quando non in loading */}
      {!isLoading && !error && (
        <div className="max-w-3xl mx-auto mt-4 flex justify-end">
          <button
            type="button"
            onClick={handleOpenCompose}
            className="flex h-10 items-center gap-1.5 rounded-full border border-ink bg-ink px-5 text-[13px] font-semibold text-on-ink transition-[filter] hover:brightness-125"
          >
            {/* Reply icon */}
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <polyline points="9 17 4 12 9 7" />
              <path d="M20 18v-2a4 4 0 0 0-4-4H4" />
            </svg>
            Rispondi
          </button>
        </div>
      )}

      {/* Dialog compose (aperto da useMailCompose) */}
      {composeDialog}
    </>
  );
}
